import purify from "dompurify";

import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { MajorPeakType } from "app/pages/website-analysis/TrafficAndEngagement/Components/single/TotalVisitsComponentHistoryHook";
import {
    TotalVisitsComponentHistoryHookButton,
    TotalVisitsComponentHistoryHookContainer,
    TotalVisitsComponentHistoryHookImage,
} from "app/pages/website-analysis/TrafficAndEngagement/Components/single/TotalVisitsComponentHistoryHook/StyledTotalVisitsComponentHistoryHook";
import { AssetsService } from "app/services/AssetsService";
import { useTranslation } from "pro-features/components/WithTranslation";

interface ITotalVisitsComponentHistoryHookProps {
    ctaRef: React.RefObject<HTMLDivElement>;
    majorPeakType: MajorPeakType;
    onHookClick: () => void;
    onCloseClicked: () => void;
    onMouseLeave: () => void;
}

export const TotalVisitsComponentHistoryHook = ({
    ctaRef,
    majorPeakType,
    onHookClick,
    onCloseClicked,
    onMouseLeave,
}: ITotalVisitsComponentHistoryHookProps) => {
    const translate = useTranslation();

    return (
        <TotalVisitsComponentHistoryHookContainer
            ref={ctaRef}
            onMouseLeave={onMouseLeave}
        >
            <TotalVisitsComponentHistoryHookButton
                type="flat"
                iconSize="xs"
                iconColor={colorsPalettes.blue[400]}
                iconName="close"
                onClick={onCloseClicked}
            />

            <Flex
                justifyContent="center"
                alignItems="center"
                gap="16px"
            >
                <TotalVisitsComponentHistoryHookImage
                    alt={translate("website_analysis.total_visits.history_hook.image_alt")}
                    src={AssetsService.assetUrl("/images/unlock_historical_data.svg")}
                />

                <Stack
                    gap="16px"
                    alignItems="center"
                >
                    <Stack gap="8px">
                        <Text
                            fontVariant="h5_2"
                            textAlign="center"
                            data-automation="total-visits-history-hook-title"
                            dangerouslySetInnerHTML={{
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                __html: purify.sanitize(
                                    translate(`website_analysis.total_visits.history_hook.${majorPeakType}.title`),
                                ),
                            }}
                        />
                        <Text
                            fontVariant="body2"
                            textAlign="center"
                            color={colorsPalettes.carbon[300]}
                        >
                            {translate(`website_analysis.total_visits.history_hook.${majorPeakType}.text`)}
                        </Text>
                    </Stack>

                    <Flex>
                        <Button onClick={onHookClick}>
                            {translate(`website_analysis.total_visits.history_hook.${majorPeakType}.button`)}
                        </Button>
                    </Flex>
                </Stack>
            </Flex>
        </TotalVisitsComponentHistoryHookContainer>
    );
};
